// @ts-nocheck
import React, { useState, useEffect } from 'react';

interface CollectionItem {
  _id: string;
  name: string;
  color?: string;
  itemCount?: number;
}

interface LibraryItem {
  _id: string;
  title: string;
  description?: string;
  sourceDomain?: string;
  sourceType?: string;
  contentType?: string;
  originalUrl: string;
  canonicalUrl?: string;
  thumbnailUrl?: string;
  author?: string;
  publisher?: string;
  publishedAt?: string;
  createdAt?: string;
  readState: 'unread' | 'reading' | 'read';
  savedState: 'saved' | 'archived' | 'deleted';
  isFavorite?: boolean;
  tags?: string[];
  collectionIds?: (string | { _id: string })[];
  note?: string;
}

interface EditLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: LibraryItem | null;
  collections: CollectionItem[];
  onSave: (
    item: LibraryItem,
    updates: {
      title: string;
      originalUrl: string;
      note: string;
      tags: string[];
      collectionIds: string[];
    }
  ) => Promise<any>;
  onOpenCreateCollection?: () => void;
  onCreateCollection?: (data: { name: string; description?: string; color?: string }) => Promise<any>;
  justCreatedCollectionId?: string | null;
}

export default function EditLinkModal({
  isOpen,
  onClose,
  item,
  collections = [],
  onSave,
  onOpenCreateCollection,
  onCreateCollection,
  justCreatedCollectionId,
}: EditLinkModalProps) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Inline collection creation state
  const [isCreatingInline, setIsCreatingInline] = useState(false);
  const [inlineCollectionName, setInlineCollectionName] = useState('');
  const [isInlineSubmitting, setIsInlineSubmitting] = useState(false);
  const [collectionFeedback, setCollectionFeedback] = useState('');

  // Synchronize state when item changes or modal opens
  useEffect(() => {
    if (item && isOpen) {
      setTitle(item.title || '');
      setUrl(item.originalUrl || item.canonicalUrl || '');
      setNote(item.note || '');
      setTagInput((item.tags || []).join(', '));

      const rawCids = item.collectionIds || [];
      const normalizedCids = rawCids
        .map((cid) => (typeof cid === 'object' && cid && cid._id ? cid._id : String(cid)))
        .filter(Boolean);
      setSelectedCollectionIds(normalizedCids);
      setErrorMessage('');
      setSuccessMessage('');
      setIsCreatingInline(false);
      setInlineCollectionName('');
      setCollectionFeedback('');
    }
  }, [item, isOpen]);

  useEffect(() => {
    if (justCreatedCollectionId) {
      setSelectedCollectionIds((prev) =>
        prev.includes(justCreatedCollectionId) ? prev : [...prev, justCreatedCollectionId]
      );
    }
  }, [justCreatedCollectionId]);

  if (!isOpen || !item) return null;

  const handleToggleCollection = (colId: string) => {
    setSelectedCollectionIds((prev) =>
      prev.includes(colId) ? prev.filter((id) => id !== colId) : [...prev, colId]
    );
  };

  const handleSelectAllCollections = () => {
    setSelectedCollectionIds(collections.map((c) => c._id));
  };

  const handleClearCollections = () => {
    setSelectedCollectionIds([]);
  };

  const handleInlineAddCollection = async () => {
    const trimmed = inlineCollectionName.trim();
    if (!trimmed) return;

    const existing = collections.find(
      (c) => c.name.toLowerCase() === trimmed.toLowerCase()
    );

    if (existing) {
      setSelectedCollectionIds((prev) =>
        prev.includes(existing._id) ? prev : [...prev, existing._id]
      );
      setInlineCollectionName('');
      setIsCreatingInline(false);
      setCollectionFeedback(`Selected existing "${existing.name}" collection`);
      setTimeout(() => setCollectionFeedback(''), 4000);
      return;
    }

    if (onCreateCollection) {
      setIsInlineSubmitting(true);
      try {
        const created = await onCreateCollection({ name: trimmed });
        const createdId = created?._id || created?.data?._id;
        if (createdId) {
          setSelectedCollectionIds((prev) =>
            prev.includes(createdId) ? prev : [...prev, createdId]
          );
        }
        setInlineCollectionName('');
        setIsCreatingInline(false);
        setCollectionFeedback(`Added and selected "${trimmed}"`);
        setTimeout(() => setCollectionFeedback(''), 4000);
      } catch (err: any) {
        setErrorMessage(err.message || 'Failed to create collection');
      } finally {
        setIsInlineSubmitting(false);
      }
    } else if (onOpenCreateCollection) {
      onOpenCreateCollection();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setErrorMessage('Please provide a title for this item.');
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);
    setSuccessMessage('Saving changes…');

    try {
      const cleanTags = tagInput
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      await onSave(item, {
        title: title.trim(),
        originalUrl: url.trim(),
        note: note.trim(),
        tags: cleanTags,
        collectionIds: selectedCollectionIds,
      });

      setSuccessMessage('Updated successfully!');
      setTimeout(() => {
        setIsSubmitting(false);
        setSuccessMessage('');
        onClose();
      }, 400);
    } catch (err: any) {
      setIsSubmitting(false);
      setSuccessMessage('');
      setErrorMessage(err.message || 'Failed to update item. Please try again.');
    }
  };

  return (
    <div className="reader-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="reader-modal-card reader-edit-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '580px' }}
      >
        <div className="reader-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                background: 'var(--reader-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--reader-accent)',
              }}
            >
              <i className="fi fi-rr-edit" style={{ fontSize: '0.85rem' }} />
            </div>
            <h3 className="reader-modal-title">Edit Link & Move Collections</h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close edit modal"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: 'var(--reader-text-muted)',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="reader-modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            {errorMessage && (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid #ef4444',
                  borderRadius: '6px',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                }}
              >
                {errorMessage}
              </div>
            )}

            {successMessage && (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'var(--reader-accent-light)',
                  border: '1px solid var(--reader-accent)',
                  borderRadius: '6px',
                  color: 'var(--reader-accent)',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <i className="fi fi-rr-spinner" style={{ animation: 'spin 1s linear infinite' }} />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Title */}
            <div className="reader-form-group">
              <label className="reader-form-label">Title *</label>
              <input
                type="text"
                className="reader-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Article or Link Title"
                required
                disabled={isSubmitting}
              />
            </div>

            {/* URL */}
            <div className="reader-form-group">
              <label className="reader-form-label">Link URL</label>
              <input
                type="url"
                className="reader-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/..."
                disabled={isSubmitting}
              />
            </div>

            {/* MOVE TO COLLECTIONS (MULTI-SELECT) */}
            <div className="reader-form-group">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '2px',
                }}
              >
                <div>
                  <label className="reader-form-label" style={{ display: 'block' }}>
                    Move to Collections
                  </label>
                  <span
                    style={{
                      fontSize: '0.74rem',
                      color: 'var(--reader-text-subtle)',
                      display: 'block',
                    }}
                  >
                    Select one or multiple collections, or uncheck all to keep in general inbox.
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {collections.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handleSelectAllCollections}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '0.72rem',
                          color: 'var(--reader-accent)',
                          cursor: 'pointer',
                          padding: '2px 4px',
                        }}
                      >
                        Select all
                      </button>
                      <span style={{ fontSize: '0.7rem', color: 'var(--reader-border)' }}>•</span>
                    </>
                  )}
                  {selectedCollectionIds.length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearCollections}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '0.72rem',
                        color: 'var(--reader-text-muted)',
                        cursor: 'pointer',
                        padding: '2px 4px',
                      }}
                    >
                      Clear
                    </button>
                  )}
                  {(onCreateCollection || onOpenCreateCollection) && (
                    <>
                      <span style={{ fontSize: '0.7rem', color: 'var(--reader-border)' }}>•</span>
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreatingInline((prev) => !prev);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '0.72rem',
                          color: 'var(--reader-accent)',
                          cursor: 'pointer',
                          padding: '2px 4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}
                      >
                        <i className="fi fi-rr-plus" style={{ fontSize: '0.65rem' }} />
                        <span>New</span>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {collections.length === 0 ? (
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: '6px',
                    border: '1px dashed var(--reader-border)',
                    background: 'var(--reader-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ fontSize: '0.8rem', color: 'var(--reader-text-muted)' }}>
                    No collections created yet.
                  </span>
                  {(onCreateCollection || onOpenCreateCollection) && (
                    <button
                      type="button"
                      className="reader-btn"
                      style={{ fontSize: '0.75rem', height: '26px', padding: '0 8px' }}
                      onClick={() => setIsCreatingInline(true)}
                    >
                      <i className="fi fi-rr-plus" style={{ fontSize: '0.65rem' }} />
                      <span>Create Collection</span>
                    </button>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                    gap: '8px',
                    maxHeight: '180px',
                    overflowY: 'auto',
                    padding: '4px 1px',
                  }}
                >
                  {collections.map((col) => {
                    const isSelected = selectedCollectionIds.includes(col._id);
                    return (
                      <div
                        key={col._id}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onClick={() => handleToggleCollection(col._id)}
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            handleToggleCollection(col._id);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 10px',
                          borderRadius: '6px',
                          border: isSelected
                            ? '1.5px solid var(--reader-accent)'
                            : '1px solid var(--reader-border)',
                          background: isSelected
                            ? 'var(--reader-accent-light)'
                            : 'var(--reader-bg)',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          userSelect: 'none',
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '4px',
                            border: isSelected
                              ? '1.5px solid var(--reader-accent)'
                              : '1.5px solid var(--reader-text-subtle)',
                            background: isSelected ? 'var(--reader-accent)' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            fontSize: '0.65rem',
                            flexShrink: 0,
                          }}
                        >
                          {isSelected && <i className="fi fi-rr-check" />}
                        </div>

                        <span
                          style={{
                            width: '9px',
                            height: '9px',
                            borderRadius: '2px',
                            backgroundColor: col.color || 'var(--reader-accent)',
                            flexShrink: 0,
                          }}
                        />

                        <span
                          style={{
                            fontSize: '0.82rem',
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? 'var(--reader-accent)' : 'var(--reader-text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                          title={col.name}
                        >
                          {col.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {isCreatingInline && (
                <div
                  style={{
                    marginTop: '8px',
                    padding: '8px 10px',
                    background: 'var(--reader-hover)',
                    border: '1px solid var(--reader-border)',
                    borderRadius: '6px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="text"
                      className="reader-input"
                      style={{ flex: 1, height: '30px', fontSize: '0.82rem', padding: '4px 8px' }}
                      placeholder="Collection name (e.g. Technology)..."
                      value={inlineCollectionName}
                      onChange={(e) => setInlineCollectionName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleInlineAddCollection();
                        } else if (e.key === 'Escape') {
                          setIsCreatingInline(false);
                          setInlineCollectionName('');
                        }
                      }}
                      autoFocus
                      disabled={isInlineSubmitting}
                    />
                    <button
                      type="button"
                      className="reader-btn reader-btn-primary"
                      style={{ height: '30px', fontSize: '0.78rem', padding: '0 10px' }}
                      onClick={handleInlineAddCollection}
                      disabled={isInlineSubmitting || !inlineCollectionName.trim()}
                    >
                      {isInlineSubmitting ? 'Adding…' : 'Add'}
                    </button>
                    <button
                      type="button"
                      className="reader-btn"
                      style={{ height: '30px', fontSize: '0.78rem', padding: '0 8px' }}
                      onClick={() => {
                        setIsCreatingInline(false);
                        setInlineCollectionName('');
                      }}
                      disabled={isInlineSubmitting}
                    >
                      Cancel
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.71rem', color: 'var(--reader-text-subtle)' }}>
                      Enter a new or existing collection name.
                    </span>
                    {onOpenCreateCollection && (
                      <button
                        type="button"
                        onClick={onOpenCreateCollection}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '0.71rem',
                          color: 'var(--reader-accent)',
                          cursor: 'pointer',
                          padding: 0,
                          textDecoration: 'underline',
                        }}
                      >
                        More options (color, description)
                      </button>
                    )}
                  </div>
                </div>
              )}

              {collectionFeedback && (
                <div
                  style={{
                    marginTop: '6px',
                    padding: '5px 8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid #10b981',
                    borderRadius: '4px',
                    color: '#10b981',
                    fontSize: '0.76rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <i className="fi fi-rr-check" style={{ fontSize: '0.7rem' }} />
                  {collectionFeedback}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '4px',
                  fontSize: '0.75rem',
                  color: 'var(--reader-text-subtle)',
                }}
              >
                <span>
                  {selectedCollectionIds.length === 0
                    ? 'Inbox / Uncategorized'
                    : selectedCollectionIds.length === 1
                    ? 'Selected: 1 collection'
                    : `Selected: ${selectedCollectionIds.length} collections`}
                </span>
              </div>
            </div>

            {/* Tags */}
            <div className="reader-form-group">
              <label className="reader-form-label">Tags (comma-separated)</label>
              <input
                type="text"
                className="reader-input"
                placeholder="tech, ai, finance, productivity"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {/* Personal Note */}
            <div className="reader-form-group">
              <label className="reader-form-label">Personal Notes & Takeaways</label>
              <textarea
                className="reader-input"
                style={{ height: '75px', padding: '8px 12px', resize: 'vertical' }}
                placeholder="Add takeaways or comments for this item..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="reader-modal-footer">
            <button
              type="button"
              className="reader-btn"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="reader-btn reader-btn-primary"
              disabled={isSubmitting || !title.trim()}
            >
              {isSubmitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
